import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaChevronRight, FaPlus } from 'react-icons/fa';
import classNames from 'classnames';
import { CommunityData } from '../../types/misc';

interface CommunitySelectorProps {
  communities: CommunityData[];
  selectedCommunity: CommunityData | null;
  onSelectCommunity: (community: CommunityData) => void;
  onCreateNew?: () => void;
}

export default function CommunitySelector({
  communities,
  selectedCommunity,
  onSelectCommunity,
  onCreateNew,
}: CommunitySelectorProps): JSX.Element {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (community: CommunityData) => {
    onSelectCommunity(community);
    setIsOpen(false);
  };

  if (!selectedCommunity) return <></>;

  return (
    <div className="relative" ref={dropdownRef}>
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-3 rounded-xl">
        {selectedCommunity.picture ? (
          <img
            src={selectedCommunity.picture}
            alt={selectedCommunity.name}
            className="h-12 w-12 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-xl font-bold text-white">
            {selectedCommunity.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col items-start">
          <span className="text-base font-semibold text-gray-900">{selectedCommunity.name}</span>
          <span className="text-sm text-gray-500">{t('general.selectedCommunity')}</span>
        </div>
        <FaChevronRight
          className={classNames('h-4 w-4 flex-shrink-0 text-gray-400 transition-transform', {
            'rotate-90': isOpen,
          })}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 w-[280px] rounded-lg border border-gray-200 bg-secondary shadow-lg">
          <div className="max-h-[400px] overflow-y-auto p-2">
            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {t('general.selectCommunity')}
            </div>
            {communities.map((community) => (
              <button
                key={community.id}
                type="button"
                onClick={() => handleSelect(community)}
                className={classNames(
                  'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left transition-colors',
                  {
                    'bg-primary-50 text-primary': selectedCommunity.id === community.id,
                    'text-gray-700 hover:bg-gray-50': selectedCommunity.id !== community.id,
                  },
                )}>
                <div className="flex items-center gap-3">
                  {community.picture ? (
                    <img src={community.picture} alt={community.name} className="h-8 w-8 rounded object-cover" />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-primary text-sm font-bold text-white">
                      {community.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{community.name}</p>
                  </div>
                </div>
                {selectedCommunity.id === community.id && <FaCheck className="h-4 w-4 flex-shrink-0 text-primary" />}
              </button>
            ))}
          </div>
          {onCreateNew && (
            <>
              <div className="border-t border-gray-200" />
              <button
                type="button"
                onClick={() => {
                  onCreateNew();
                  setIsOpen(false);
                }}
                className="hover:bg-primary-50 flex w-full items-center gap-3 rounded-md px-5 py-3 text-left text-sm font-medium text-primary transition-colors">
                <FaPlus className="h-4 w-4" />
                <span>{t('page.communityOverview.new')}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
